import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { ExplicitToggle } from '../../components/ui/ExplicitToggle';
import { Button } from '../../components/ui/Button';
import { EmojiButton } from '../../components/ui/EmojiPicker';
import { Card } from '../../components/ui/Card';
import { Field } from '../../components/ui/Field';
import { WaveformScan } from '../../components/ui/Loader';
import { copy } from '../../config/copy';
import { ApiError, api } from '../../lib/api';
import { useEmojiComposer } from '../../lib/emoji';
import { pollUntil, uploadTrack } from '../../lib/upload';
import { ACCEPTED_AUDIO, contentTypeOf, describeFile, isAcceptedAudio } from './accepted';

interface Genre {
  id: number;
  name: string;
  slug: string;
}

interface TrackStatus {
  id: number;
  status: 'pending' | 'processing' | 'ready' | 'failed';
  failure_reason: string | null;
}

/**
 * Four states, one screen.
 *
 * `choosing → uploading → processing → done`, and it is one screen rather than a
 * wizard because the middle two are not steps the user takes. They are waits,
 * and a wait that replaces the page reads as "something else started" rather
 * than "the same thing is still going".
 *
 * The two waits are told apart deliberately:
 *
 * - **Uploading** has a real percentage and a cancel, because the bytes are
 *   leaving this machine and the browser can stop them.
 * - **Processing** has neither, because the bytes have landed and the work is
 *   now happening somewhere the browser cannot see or interrupt.
 *
 * One shared spinner for both would promise a cancel that does not exist, and
 * an indeterminate bar during the upload would throw away the one number we
 * genuinely have.
 */
type Phase = 'choosing' | 'uploading' | 'processing' | 'timeout' | 'failed';

const SELECT_CLASSES = [
  'rounded-[var(--radius-control)] border border-line bg-app px-3 py-2.5 text-sm text-ink',
  'focus:border-line-strong',
].join(' ');

export function UploadPage() {
  const navigate = useNavigate();

  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [genreId, setGenreId] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'unlisted' | 'private'>('public');
  const [explicit, setExplicit] = useState(false);

  const [genres, setGenres] = useState<Genre[]>([]);
  const [phase, setPhase] = useState<Phase>('choosing');
  const [percent, setPercent] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [trackId, setTrackId] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);

  const emoji = useEmojiComposer(description, setDescription);

  const abortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    api
      .get<{ items: Genre[] }>('/genres')
      .then((data) => setGenres(data.items))
      // A missing genre list is not a reason to block an upload: the field is
      // optional, so the select just stays at "Not sure yet".
      .catch(() => setGenres([]));
  }, []);

  /*
   * Warn before leaving mid-upload.
   *
   * Closing the tab during a multipart upload abandons parts in the bucket that
   * nothing will ever complete - and, more to the point, throws away however
   * long the upload had been running. Only registered while it would mean
   * something; a permanent handler would nag on the way out of an idle form.
   */
  useEffect(() => {
    if (phase !== 'uploading') return;

    const warn = (event: BeforeUnloadEvent) => event.preventDefault();

    window.addEventListener('beforeunload', warn);

    return () => window.removeEventListener('beforeunload', warn);
  }, [phase]);

  function choose(picked: File | null | undefined) {
    setFileError(null);

    if (!picked) return;

    if (!isAcceptedAudio(picked)) {
      setFileError(`We can't process that format. Try ${ACCEPTED_AUDIO.label}.`);

      return;
    }

    setFile(picked);

    // The filename minus its extension, as a starting title. Almost always
    // close to right, and always easier to correct than to type from nothing.
    setTitle((current) => current || picked.name.replace(/\.[^.]+$/, ''));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();

    if (!file || phase === 'uploading' || phase === 'processing') return;

    setError(null);
    setPercent(0);
    setPhase('uploading');

    const controller = new AbortController();
    abortRef.current = controller;

    let id: number;

    try {
      id = await uploadTrack(
        file,
        {
          title: title.trim() || file.name,
          content_type: contentTypeOf(file),
          size_bytes: file.size,
          genre_id: genreId ? Number(genreId) : null,
          description: description.trim() || null,
          visibility,
          explicit,
        },
        { onProgress: setPercent, signal: controller.signal },
      );
    } catch (caught) {
      abortRef.current = null;

      // Cancelling is not failing. Back to the form with the fields intact, so
      // somebody who cancelled to fix a title has not lost the rest.
      if (caught instanceof DOMException && caught.name === 'AbortError') {
        setPhase('choosing');
        setPercent(0);

        return;
      }

      setError(
        caught instanceof ApiError
          ? caught.message
          : caught instanceof Error
            ? caught.message
            : 'The upload failed.',
      );
      setPhase('failed');

      return;
    }

    abortRef.current = null;
    setTrackId(id);
    setPhase('processing');

    /*
     * Poll until it is playable.
     *
     * Five minutes, because the job is two loudness passes, two renditions and
     * a waveform, and a long track is not quick. Timing out here is not a
     * failure - the work carries on regardless - so that path says where to find
     * the track rather than that something went wrong.
     *
     * This is the interim mechanism. Phase 4 replaces it with `track.ready` on
     * `private-user.{id}` (§8.1) and this becomes the fallback.
     */
    const settled = await pollUntil<TrackStatus>(
      () => api.get<TrackStatus>(`/tracks/${id}`),
      (track) => track.status === 'ready' || track.status === 'failed',
      { intervalMs: 2000, timeoutMs: 300_000 },
    );

    if (!settled) {
      setPhase('timeout');

      return;
    }

    const track = await api.get<TrackStatus>(`/tracks/${id}`).catch(() => null);

    if (track?.status === 'failed') {
      setError(track.failure_reason ?? "We couldn't process that file.");
      setPhase('failed');

      return;
    }

    navigate(`/tracks/${id}`);
  }

  function reset() {
    setFile(null);
    setTitle('');
    setDescription('');
    setPercent(0);
    setError(null);
    setFileError(null);
    setTrackId(null);
    setPhase('choosing');
  }

  const busy = phase === 'uploading' || phase === 'processing';

  return (
    <div className="mx-auto max-w-2xl px-6 py-12">
      <h1 className="text-2xl font-semibold tracking-tight text-ink">{copy['cta.upload']}</h1>
      <p className="mt-1.5 text-sm text-soft">
        {ACCEPTED_AUDIO.label}, up to 500MB. Upload the best quality you have — we make the streaming
        versions from it.
      </p>

      <Card className="mt-6 p-6">
        <form onSubmit={submit} className="flex flex-col gap-5" data-upload-form>
          {/* ----------------------------------------------------------- */}
          {/* The file                                                     */}
          {/* ----------------------------------------------------------- */}
          <div
            onDragOver={(event) => {
              event.preventDefault();

              if (!busy) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);

              if (!busy) choose(event.dataTransfer.files[0]);
            }}
            className={[
              'rounded-[var(--radius-card)] border border-dashed px-6 py-8 text-center transition-colors',
              dragging ? 'border-accent bg-raised' : 'border-line-strong',
            ].join(' ')}
            data-dropzone
          >
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPTED_AUDIO.accept}
              className="sr-only"
              data-file-input
              disabled={busy}
              onChange={(event) => choose(event.target.files?.[0])}
            />

            {file ? (
              <div>
                <p className="text-sm font-medium text-ink" data-chosen-file>
                  {file.name}
                </p>
                <p className="meta-caps mt-1.5">{describeFile(file)}</p>

                {!busy && (
                  <Button
                    variant="ghost"
                    className="mt-3"
                    onClick={() => inputRef.current?.click()}
                  >
                    Choose a different file
                  </Button>
                )}
              </div>
            ) : (
              <div>
                <p className="text-sm text-soft">Drag a file here</p>
                <Button variant="secondary" className="mt-3" onClick={() => inputRef.current?.click()}>
                  Browse
                </Button>
              </div>
            )}
          </div>

          {fileError && <Alert tone="error">{fileError}</Alert>}

          {/* ----------------------------------------------------------- */}
          {/* The details                                                  */}
          {/* ----------------------------------------------------------- */}
          <Field
            label="Title"
            name="title"
            value={title}
            maxLength={120}
            disabled={busy}
            required
            onChange={(event) => setTitle(event.target.value)}
          />

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="track-genre" className="text-sm font-medium text-soft">
                Genre
              </label>
              <select
                id="track-genre"
                className={SELECT_CLASSES}
                value={genreId}
                disabled={busy}
                onChange={(event) => setGenreId(event.target.value)}
              >
                <option value="">Not sure yet</option>
                {genres.map((genre) => (
                  <option key={genre.id} value={genre.id}>
                    {genre.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="track-visibility" className="text-sm font-medium text-soft">
                Who can hear it
              </label>
              <select
                id="track-visibility"
                className={SELECT_CLASSES}
                value={visibility}
                disabled={busy}
                data-visibility
                onChange={(event) =>
                  setVisibility(event.target.value as 'public' | 'unlisted' | 'private')
                }
              >
                <option value="public">Everyone</option>
                <option value="unlisted">Anyone with the link</option>
                <option value="private">Only me</option>
              </select>
              <p className="text-xs text-faint">
                {/*
                  Said plainly, because the difference is the whole reason the
                  media gate exists (§4.4) and "unlisted" means different things
                  on different services.
                */}
                {visibility === 'public'
                  ? 'Shows up on your profile and in discovery.'
                  : visibility === 'unlisted'
                    ? 'Hidden from your profile. Anyone you send the link to can play it.'
                    : 'Nobody but you can play it.'}
              </p>
            </div>
          </div>

          {/*
            Its own row, under the pair rather than beside them.

            It is a statement about the work rather than a setting like the two
            above, and it carries three lines of consequence - squeezed into a
            grid cell it wrapped four times and made the row twice the height of
            the field next to it.
          */}
          <ExplicitToggle value={explicit} onChange={setExplicit} disabled={busy} />

          <div className="flex flex-col gap-1.5">
            <label htmlFor="track-description" className="text-sm font-medium text-soft">
              Description
            </label>
            <textarea
              id="track-description"
            ref={emoji.ref as React.RefObject<HTMLTextAreaElement>}
              className={`${SELECT_CLASSES} min-h-24 resize-y placeholder:text-faint`}
              value={description}
              maxLength={2000}
              disabled={busy}
              placeholder="Credits, gear, what it's about — optional."
              onChange={(event) => setDescription(event.target.value)}
            />
            <div className="-mt-0.5 flex">
              <EmojiButton onSelect={emoji.insert} direction="down" align="left" />
            </div>
          </div>

          {error && <Alert tone="error">{error}</Alert>}

          {/* ----------------------------------------------------------- */}
          {/* The two waits                                                */}
          {/* ----------------------------------------------------------- */}
          {phase === 'uploading' && (
            <div data-upload-progress>
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-ink">Uploading</span>
                <span className="meta-caps tabular-nums" data-upload-percent>
                  {percent}%
                </span>
              </div>
              <div
                className="mt-2 h-1.5 overflow-hidden rounded-[var(--radius-pill)] bg-line"
                role="progressbar"
                aria-label="Upload progress"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={percent}
              >
                <div
                  className="h-full rounded-[var(--radius-pill)] bg-accent transition-[width] duration-200"
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>
          )}

          {phase === 'processing' && (
            <div className="flex items-start gap-4" data-upload-processing>
              {/*
                The scanning waveform, not a spinner. This wait is the encoder
                reading the file, and a lit segment travelling along a wave says
                that - it is the difference between "the site is busy" and "the
                site is working on my track", which is what somebody standing
                over a two-minute transcode wants to know.
              */}
              <WaveformScan className="mt-0.5 w-24 shrink-0" />
              <div>
                <p className="text-sm text-ink">Your file is up. We're building the streaming versions.</p>
                <p className="mt-1 text-xs text-faint">
                  Roughly as long as the track itself. You can leave this page — it'll be on your
                  profile when it's done.
                </p>
              </div>
            </div>
          )}

          {phase === 'timeout' && trackId !== null && (
            <Alert tone="info">
              Still processing. It's taking longer than usual, but it hasn't failed — it'll appear on
              your profile when it's ready.
            </Alert>
          )}

          <div className="flex items-center gap-3">
            {phase === 'uploading' ? (
              <Button variant="secondary" onClick={() => abortRef.current?.abort()}>
                Cancel
              </Button>
            ) : phase === 'failed' ? (
              <Button onClick={reset}>Start over</Button>
            ) : phase === 'timeout' ? (
              <Button onClick={reset}>Upload another</Button>
            ) : (
              <Button type="submit" disabled={!file} loading={phase === 'processing'}>
                {copy['cta.upload']}
              </Button>
            )}
          </div>
        </form>
      </Card>
    </div>
  );
}
